import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Home,
  FileText,
  Brain,
  BarChart3,
  Upload
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export const Sidebar: React.FC = () => {
  const { userProfile } = useAuth();
  const location = useLocation();

  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: Home },
    { name: 'Documents', href: '/documents', icon: FileText },
    { name: 'Study Tools', href: '/study', icon: Brain },
    { name: 'Progress', href: '/progress', icon: BarChart3 },
    { name: 'Upload', href: '/upload', icon: Upload },
  ];

  return (
    <div className="h-full bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-700 flex flex-col">
      {/* User Profile Section - Compact */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-3">
          <div className="h-8 w-8 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center flex-shrink-0">
            {userProfile?.photoURL ? (
              <img src={userProfile.photoURL} alt="Profile" className="h-8 w-8 rounded-full" />
            ) : (
              <span className="text-white text-sm font-medium">
                {userProfile?.displayName?.charAt(0) || 'G'}
              </span>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
              {userProfile?.displayName || 'Guest User'}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {userProfile?.studyStats?.studyMinutes || 0}m studied
            </p>
          </div>
        </div>
      </div>

      {/* Primary Navigation - Compact */}
      <nav className="flex-1 px-3 py-4">
        <ul className="space-y-1">
          {navigation.map((item) => (
            <li key={item.name}>
              <Link
                to={item.href}
                className={`group relative flex items-center gap-x-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200 ${
                  location.pathname === item.href
                    ? 'bg-primary-50 text-primary-600 dark:bg-primary-900/20 dark:text-primary-400 shadow-sm'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-primary-400 dark:hover:bg-gray-800'
                }`}
              >
                <item.icon className="h-4 w-4 shrink-0" />
                <span className="truncate">{item.name}</span>
                {location.pathname === item.href && (
                  <motion.div
                    layoutId="sidebar-indicator"
                    className="absolute left-0 top-0 bottom-0 w-1 bg-primary-600 dark:bg-primary-400 rounded-r-md"
                  />
                )}
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Stats Section - Compact */}
      <div className="p-4 border-t border-gray-200 dark:border-gray-700">
        <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">
          Progress
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Docs</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {userProfile?.studyStats?.documentsUploaded || 0}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Cards</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {userProfile?.studyStats?.flashcardsCreated || 0}
            </span>
          </div>
          <div className="flex justify-between text-xs">
            <span className="text-gray-600 dark:text-gray-400">Quizzes</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {userProfile?.studyStats?.quizzesCompleted || 0}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};